import { HttpResponseCodes } from '../http/axios/response';
import I18n from '../i18n';

import request from '@module/http/axios/request';

type Config = {
  path: string;
  method?: string;
  pathExt?: boolean;
};

type Options = {
  pathExtValues?: Record<string, string>;
};

export default function Apify<Methods>(
  setup: Record<keyof Methods, Config>
): Record<keyof Methods, (...args: unknown[]) => unknown> {
  // This method generates requests based on the setup
  function generateRequest(config: Config) {
    return function generateApi(
      params: { name: string; value: string } = null,
      options: Options = {}
    ) {
      if (!config) {
        throw new Error('InvalidApiCall');
      }

      // Figure out parameters
      let path = config.path;

      // Do we like to extend/replace the path with a variable?
      if (config && config.pathExt && options.pathExtValues) {
        Object.keys(options.pathExtValues).forEach((pathExtName) => {
          path = path.replace(
            `{${pathExtName}}`,
            options.pathExtValues[pathExtName]
          );
        });
      }

      return request({
        method: config.method,
        path,
        params,
      }).catch((error) => {
        if (
          error.status === HttpResponseCodes.UNAUTHORIZED &&
          error.body.message === 'Unauthorized'
        ) {
          // eslint-disable-next-line no-alert
          alert(I18n.t('notification.logged_out'));
          location.reload();
          return;
        }

        // Throw the error again to continue execution, as this error will be caught
        // in other parts of the code.
        throw error;
      });
    };
  }

  // Create an object for all these API calls
  const transformed = {} as Record<
    keyof Methods,
    ReturnType<typeof generateRequest>
  >;

  // Loop through all the calls.
  for (const key in setup) {
    transformed[key] = generateRequest(setup[key]);
  }

  return transformed;
}
